import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';
import { DEFAULT_PAGE_SIZE } from '../config/config';
import ProductService from '../services/ProductService';
import { PaginationParams } from '../types';
import { transformToValueLabel } from '../utils/functions';


class ProductStore {
    loading: boolean = false;
    productList: any = [];
    productListByCategory: any = [];
    product: any = {
        prices: [],
        productProperties: [],
        files: [],
        categories: [],
    };
    stockList: any = [];
    productMassListError: any = [];
    arrayImageUpload: any = [];
    arrayImageUrl: any = [];
    listPrice: any = []
    removeImageArray: any = [];
    categoriesSelected: any = [];
    sort?: string = '';
    totalPages: number = 0;
    page: number = 0;
    totalElements: number = 0;
    size: number = DEFAULT_PAGE_SIZE;
    defaultparams = {
        size: DEFAULT_PAGE_SIZE,
        page: 0,
        sort: 'created,desc',
    }

    constructor() {
        makeAutoObservable(this);
    }

    reset() {
        this.product = {
            productProperties: [],
            files: [],
            categories: [],
            active: false,
        };
        this.arrayImageUpload = [];
        this.arrayImageUrl = [];
        this.listPrice = [];
        this.removeImageArray = [];
        this.categoriesSelected = [];
        this.stockList = [];
    }

    async getList(params: PaginationParams, type: string) {
        this.loading = true;
        const response = await ProductService.getList(params, type);

        runInAction(() => {
            if (!response.error) {
                this.productList = response.data.content;
                this.totalPages = response.data.totalPages;
                this.page = response.data.number;
                this.totalElements = response.data.totalElements;
                this.size = response.data.size;
            }
            this.loading = false;
        });

        return response;
    }


    async getProductByCategory(slug: string, params: PaginationParams) {
        this.loading = true;
        const response = await ProductService.getProductByCategory(slug, params);

        runInAction(() => {
            if (!response.error) {
                this.productListByCategory = response.data.content;
                this.totalPages = response.data.totalPages;
                this.page = response.data.number;
                this.totalElements = response.data.totalElements;
                this.size = response.data.size;
            } else {
                this.productListByCategory = [];
                this.totalPages = 1;
                this.page = 0;
                this.totalElements = 0;
                this.size = 20;
            }
            this.loading = false;
        });

        return response;
    }


    async getProductByUuid(uuid: string) {
        this.loading = true;
        const response = await ProductService.getProductByUuid(uuid);

        if (response.error) {
            message.error('Erro ao buscar Produto');
            return;
        }

        runInAction(async () => {
            this.product = response.data;
            this.product.prices = response.data.prices;
            this.categoriesSelected = await this.extractUUIDs(this.product?.categories);
            this.listPrice = response.data.prices.filter((item: any) => item.show === true);
            this.loading = false;

        });
        return response.data;
    }

    async createProduct(data: any) {
        this.loading = true;

        const removeFiles = data.files = data.files.filter((file: any) => file.new !== true);
        data.files = removeFiles

        const originalUUIDs = await this.extractUUIDs(this.product?.categories);
        const removeCategoriesList = originalUUIDs.filter((uuid) => !this.categoriesSelected.includes(uuid));

        const mergedCategories = [...new Set([...originalUUIDs, ...this.categoriesSelected])]
            .filter((uuid) => !removeCategoriesList.includes(uuid));

        const transformedArray = mergedCategories.map(uuid => ({ uuid }));
        data.categories = transformedArray;

        const response = await ProductService.createProduct(data);

        if (response.error) {
            message.error(`Erro ao criar Produto`);
            this.product = data;
        } else {
            message.success('Produto cadastrado com sucesso!');
            await this.editCategories(response.data.uuid);
            await this.saveImageUrl(response.data.uuid);
            await this.saveImageUpload(response.data.uuid);
        }

        this.loading = false;
        return response;
    }


    async editProduct(data: any, uuid: string | undefined) {
        this.loading = true;

        const removeFiles = data.files = data.files.filter((file: any) => file.new !== true);
        data.files = removeFiles
        data.categories = [];
        const response = await ProductService.editProduct(data, uuid);


        if (response.error) {
            message.error(`Erro ao editar Produto`);
        } else {
            message.success('Produto editado com sucesso!');
            await this.editCategories(response.data.uuid);
            await this.saveImageUrl(response.data.uuid);
            await this.saveImageUpload(response.data.uuid);
            await this.deleteImage();
            this.arrayImageUpload = [];
            this.arrayImageUrl = [];
        }

        this.loading = false;
        return response;
    }


    async extractUUIDs(categories: any[]) {
        return categories.map((category) => category.uuid);
    };


    async searchProduct(searchTerm: string) {
        this.loading = true;
        const response = await ProductService.searchProduct(searchTerm);

        if (!response.data.error) {
            const selectCustomerList = transformToValueLabel(response.data.content, 'uuid', 'name');

            if (response.data.content.length === 0) {
                message.error('Nenhum produto foi encontrado para a busca realizada.');
            }
            this.loading = false;
            return selectCustomerList;
        }
        this.loading = false;
    }

    async deleteProduct(uuid: string) {
        this.loading = true;
        const response = await ProductService.deleteProduct(uuid);

        if (response.error) {
            message.error(`Erro ao deletar Produto`);
        } else {
            message.success('Produto deletado com sucesso!');
        }

        this.product = response.data;
        this.loading = false;
        return response;
    }

    async deleteProductAll(uuid: string) {
        this.loading = true;
        const response = await ProductService.deleteProduct(uuid);
        this.product = response.data;
        this.loading = false;
        return response;
    }

    async searchProductBySkuCode(searchTerm: string) {
        const response = await ProductService.searchProductAllBySku(searchTerm);

        if (!response.error) {
            return response.data
        }
    }

    async updateFakePrice(values: any, clear: boolean) {
        if (clear === false) {
            const filterPrice = this.product.prices.filter((item: any) => {
                return (
                    (values.tableId ? item.tableId === values.tableId : true) &&
                    (values.center ? item.center === values.center : true) &&
                    (values.unit ? item.nameMeasurement === values?.unit?.label : true) &&
                    (values.fakePrice?.value !== 'Todos' ? item.show === values.fakePrice.value : true)
                );
            });
            this.listPrice = filterPrice;
        } else {
            this.listPrice = this.product.prices.filter((item: any) => item.show === true);
        }
    }

    // Regras de Especificação
    async addSpecification(data: any) {
        this.product.productProperties.push(data);
    }

    async removeSpecification(uuid: string) {
        this.product.productProperties = this.product.productProperties.filter((item: any) => item.uuid !== uuid);
    }


    // Regras de Categoria
    async addCategories(category: any) {
        this.categoriesSelected = category.checked;
    }

    async editCategories(productUuid: string) {
        if (this.product.categories.length > 0 || this.categoriesSelected.length > 0) {
            const originalUUIDs = await this.extractUUIDs(this.product?.categories);
            const removeCategoriesList = originalUUIDs.filter((uuid) => !this.categoriesSelected.includes(uuid));

            // Adiciona as categorias
            const addCategoriesList = this.categoriesSelected.filter(
                (uuid: string) => !originalUUIDs.includes(uuid)
            );

            const addCategoryPromises = addCategoriesList.map((uuid: string) =>
                ProductService.addCategoryProduct(uuid, [{ uuid: productUuid }])
            );

            // Remove as categorias
            const removeCategoryPromises = removeCategoriesList.map((uuid: string) =>
                ProductService.removeCategoryProduct(uuid, [{ productUuid: productUuid }])
            );

            await Promise.all([...addCategoryPromises, ...removeCategoryPromises]);
        }
    }

    //Desvincular Produto
    async unlinkProduct(product: any) {
        product.baseProduct = null;
        const response = await ProductService.editProduct(product, product.uuid);

        if (response.error) {
            message.error(`Erro ao desvincular Produto`);
        } else {
            message.success('Produto desvinculado com sucesso!');
            this.getProductByUuid(product.uuid);
        }
    }

    // Regras Imagem
    async addImageUrl(file: any) {
        this.product.files.push(file);
        this.arrayImageUrl.push(file);
    }

    async addImageUpload(file: any) {
        this.product.files.push(file);
        this.arrayImageUpload.push(file);
    }

    async removeImage(file: any) {
        if (file.new !== false) {
            this.removeImageArray.push(file);
        }

        const removeItem = this.product.files.filter((item: any) => item.uuid !== file.uuid);
        this.product.files = removeItem;

        const removeImageUpload = this.arrayImageUpload.filter((item: any) => item.uuid !== file.uuid);
        this.arrayImageUpload = removeImageUpload;

        const removeImageUrl = this.arrayImageUrl.filter((item: any) => item.uuid !== file.uuid);
        this.arrayImageUrl = removeImageUrl;
    }

    async saveImageUrl(productUuid: string) {
        if (this.arrayImageUrl.length > 0) {
            const uploadPromises = this.arrayImageUrl.map((newImage: any) => {
                // A variável newImage deve ser um objeto File
                const file = newImage.file; // Ajuste conforme a estrutura do seu objeto

                const metadata = {
                    metaTags: newImage.metaTags,
                    ordering: newImage.ordering,
                    path: newImage.path,
                };

                const formData = new FormData();
                formData.append('file', file); // O arquivo deve ser um objeto File
                formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));

                return ProductService.addImageURL(productUuid, formData);
            });

            // Aguarda todas as promessas serem resolvidas
            await Promise.all(uploadPromises);
        }
    }


    async saveImageUpload(productUuid: string) {
        if (this.arrayImageUpload.length > 0) {
            const uploadPromises = this.arrayImageUpload.map((newImage: any) => {
                // A variável newImage deve ser um objeto File
                const file = newImage.file; // Ajuste conforme a estrutura do seu objeto

                const metadata = {
                    metaTags: newImage.metaTags,
                    ordering: newImage.ordering,
                    path: newImage.path,
                };

                const formData = new FormData();
                formData.append('file', file); // O arquivo deve ser um objeto File
                formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));

                return ProductService.addImageUpload(productUuid, formData);
            });

            // Aguarda todas as promessas serem resolvidas
            await Promise.all(uploadPromises);
        }
    }

    async deleteImage() {
        const filterOldImage = this.removeImageArray.filter((item: any) => !item.new);

        if (filterOldImage.length > 0) {
            const deletePromises = filterOldImage.map((image: any) =>
                ProductService.deleteImage(image.uuid)
            );

            await Promise.all(deletePromises);
        }
    }

    async massProductUpload(file: any) {
        this.productMassListError = [];
        this.loading = true;
        const formData = new FormData();
        formData.append('file', file);

        const result = await ProductService.massProductUpload(formData);

        if (result.error) {
            message.error(`Erro ao importar Produtos`);
            this.productMassListError = result.details.response.data.errors;
        } else {
            message.success('Produtos importados com sucesso!');
        }

        this.loading = false;
        return result;
    }

    async massProductUpdateUpload(file: any) {
        this.productMassListError = [];
        this.loading = true;
        const formData = new FormData();
        formData.append('file', file);

        const result = await ProductService.massProductUpdateUpload(formData);

        if (result.error) {
            message.error(`Erro ao importar Produtos`);
            this.productMassListError = result.details.response.data.errors;
        } else {
            message.success('Produtos importados com sucesso!');
        }

        this.loading = false;
        return result;
    }
}

const productStore = new ProductStore();
export default productStore;